import React from 'react';
import { useHistory } from 'react-router-dom';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import { Row } from 'components/Layout';
import { ALLOCATION_HISTORY } from 'adminConstants/routes';
import EditActionsButtons from './EditActionsButtons';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type ActionsRowProps = {
  hasUploadErrors: boolean;
  existingAllocationId?: string;
  isEditing: boolean;
  isAutoAllocation: boolean;
  hasErrorInAllocation: boolean;
  containsExitedShareholdings: boolean;
  containsExportedTransactionFees: boolean;
  anyInvestorsOverAllocated: boolean;
  hasAnyInvestorsAllocated: boolean;
  createDraftAllocationMutation: {
    isPending: boolean;
  };
  createShareAllocationsMutation: {
    isPending: boolean;
  };
  editShareAllocationsMutation: {
    isPending: boolean;
  };
  disabledSubmitTooltip?: string;
  onReprocessAllocation: () => void;
  onDismissEdit: () => void;
  onDraftSubmit: () => void;
  onSubmitClick: () => void;
  onValidationAndSubmit: () => void;
  onDownloadAllocation: () => void;
  hasAmendmentsFile?: boolean;
  onReupload?: () => void;
};

const ActionsRow: React.FC<ActionsRowProps> = ({
  hasUploadErrors,
  existingAllocationId,
  isEditing,
  isAutoAllocation,
  hasErrorInAllocation,
  containsExitedShareholdings,
  containsExportedTransactionFees,
  anyInvestorsOverAllocated,
  hasAnyInvestorsAllocated,
  createDraftAllocationMutation,
  createShareAllocationsMutation,
  editShareAllocationsMutation,
  disabledSubmitTooltip,
  onReprocessAllocation,
  onDismissEdit,
  onDraftSubmit,
  onSubmitClick,
  onValidationAndSubmit,
  onDownloadAllocation,
  hasAmendmentsFile,
  onReupload,
}) => {
  const history = useHistory();
  const [canCreateAllocations] = useHasPermissions([
    RolePermission.CreateAllocationUpload,
  ]);
  const handleBackClick = () => {
    history.push(ALLOCATION_HISTORY);
  };
  if (!existingAllocationId && canCreateAllocations) {
    return (
      <Row justify="space-between">
        <ButtonGroup align="left">
          {!isEditing && (
            <Button variant="outlined" onClick={onReprocessAllocation}>
              {isAutoAllocation ? 'Reprocess allocation' : 'Back'}
            </Button>
          )}
        </ButtonGroup>

        {!hasUploadErrors && (
          <>
            {isEditing ? (
              <EditActionsButtons
                onDismissEdit={onDismissEdit}
                onSaveEdit={onSubmitClick}
                disabled={
                  containsExitedShareholdings ||
                  containsExportedTransactionFees ||
                  anyInvestorsOverAllocated ||
                  !hasAnyInvestorsAllocated ||
                  createDraftAllocationMutation.isPending
                }
                disabledTooltip={disabledSubmitTooltip}
                loading={
                  createShareAllocationsMutation.isPending ||
                  editShareAllocationsMutation.isPending
                }
                align="right"
                showReupload={hasAmendmentsFile}
                onReupload={onReupload}
              />
            ) : (
              <ButtonGroup>
                <Button
                  variant="contained"
                  onClick={onDraftSubmit}
                  loading={createDraftAllocationMutation.isPending}
                >
                  Save as draft
                </Button>
                <Button
                  variant="contained"
                  color={hasErrorInAllocation ? 'warning' : 'primary'}
                  disabled={
                    containsExitedShareholdings ||
                    containsExportedTransactionFees ||
                    anyInvestorsOverAllocated ||
                    !hasAnyInvestorsAllocated ||
                    createDraftAllocationMutation.isPending
                  }
                  onClick={onSubmitClick}
                  tooltip={disabledSubmitTooltip}
                  loading={
                    createShareAllocationsMutation.isPending ||
                    editShareAllocationsMutation.isPending
                  }
                >
                  {createShareAllocationsMutation.isPending
                    ? 'Creating allocation...'
                    : hasErrorInAllocation
                    ? 'Ignore all errors and submit'
                    : 'Submit'}
                </Button>
              </ButtonGroup>
            )}
          </>
        )}
      </Row>
    );
  }

  return (
    <>
      {isEditing && (
        <EditActionsButtons
          onDismissEdit={onDismissEdit}
          onSaveEdit={onValidationAndSubmit}
          disabled={
            editShareAllocationsMutation.isPending || anyInvestorsOverAllocated
          }
          disabledTooltip={disabledSubmitTooltip}
          loading={editShareAllocationsMutation.isPending}
          align="right"
          showReupload={hasAmendmentsFile}
          onReupload={onReupload}
        />
      )}
      {!isEditing && (
        <ButtonGroup align="left">
          <Button variant="outlined" onClick={handleBackClick}>
            Back
          </Button>
          <Button variant="outlined" onClick={onDownloadAllocation}>
            Download allocation
          </Button>
        </ButtonGroup>
      )}
    </>
  );
};

export default ActionsRow;
